import PropTypes from "prop-types";
import React, { Component } from "react";
import GoogleMapsApi, { MapsApiFailure } from "./maps_api";
import FindLocation from "./find_location";

export default function withFindLocation(ComponentToWrap) {
  return class WithFindLocations extends Component {
    state = {
      businessName: "",
      locations: null,
      enableFindLocation: false
    };
    _googleMapsApi = null;
    _fetchPlacesTimeout = null;

    static get defaultProps() {
      return {
        selectedLocation: null,
        googleMapsCountries: [],
        isGoogleMapsFailure: false,
        errorMessage: null,
        isProcessing: false,
        containerClassName: undefined,
        isDisabled: false,
        onFocus: () => {},
      };
    }

    static get propTypes() {
      return {
        selectedLocation: PropTypes.object,
        googleMapsCountries: PropTypes.array,
        errorMessage: PropTypes.string,
        isProcessing: PropTypes.bool,
        withControls: PropTypes.bool,
        title: PropTypes.string,
        message: PropTypes.string,
        searchFieldLabel: PropTypes.string,
        searchFieldPlaceholder: PropTypes.string,
        isRequired: PropTypes.bool,
        renderBusinessNameField: PropTypes.func,
        getInitialData: PropTypes.func,
        isDisabled: PropTypes.bool,
        onFocus: PropTypes.func
      };
    }

    componentDidMount() {
      if (this.props.getInitialData) {
        this.props.getInitialData();
      }
      this._initGoogleMapsApi();
    }

    componentWillUnmount() {
      if (this._fetchPlacesTimeout) {
        clearTimeout(this._fetchPlacesTimeout);
      }
    }

    render() {
      const isDisabled = this._isDisabled();
      return (
        <ComponentToWrap
          {...this.props}
          selectedLocation={this.props.selectedLocation}
          onUseLocation={this._onUseLocation}
          isDisabled={isDisabled}
          errorMessage={this.state.isGoogleMapsFailure}
          onEnterLocationManually={this._onEnterLocationManually}
          content={
            <FindLocation
              containerClassName={this.props.containerClassName}
              businessName={this.state.businessName}
              locations={this.state.locations}
              selectedLocation={this.props.selectedLocation}
              isDisabled={isDisabled}
              errorMessage={this.state.isGoogleMapsFailure}
              isProcessing={this.props.isProcessing}
              renderBusinessNameField={this.props.renderBusinessNameField}
              onChangeBusinessName={this._onChangeBusinessName}
              onSelectLocation={this._onSelectLocation}
              onEnterLocationManually={this._onEnterLocationManually}
              onCancelSelectLocation={this._onCancelSelectLocation}
              onUseLocation={this._onUseLocation}
              withControls={this.props.withControls}
              title={this.props.title}
              message={this.props.message}
              searchFieldLabel={this.props.searchFieldLabel}
              searchFieldLabelSelectedLocation={
                this.props.searchFieldLabelSelectedLocation
              }
              searchFieldPlaceholder={this.props.searchFieldPlaceholder}
              onFocus={this.props.onFocus}
            />
          }
        />
      );
    }

    _isDisabled() {
      return (
        !this.state.enableFindLocation ||
        this.props.isProcessing ||
        this.props.isDisabled
      );
    }

    _initGoogleMapsApi() {
      if (!this._googleMapsApi) {
        // Talks to this plugin's backend only; no Google key in the browser.
        this._googleMapsApi = new GoogleMapsApi();
      }
      this.setState(state => ({
        ...state,
        enableFindLocation: true
      }));
    }

    _onChangeBusinessName = businessName => {
      this.setState(state => ({
        ...state,
        businessName
      }));

      // Cancel any pending request from a previous keystroke.
      if (this._fetchPlacesTimeout) {
        clearTimeout(this._fetchPlacesTimeout);
        this._fetchPlacesTimeout = null;
      }

      // Require a minimum length so single characters don't burn quota.
      if (businessName.trim().length < 3) {
        this.setState(state => ({
          ...state,
          locations: null
        }));
        return;
      }

      // Debounce: only fetch once the user pauses typing.
      this._fetchPlacesTimeout = setTimeout(() => {
        this._googleMapsApi
          .fetchPlaces(businessName)
          .then(places => {
            this.setState(
              state => ({
                ...state,
                locations: convertGmapsPlacesToLocations(places)
              }),
              () => {}
            );
          })
          .catch(error => {
            this.setState(state => ({
              ...state,
              locations: [],
              isGoogleMapsFailure: error instanceof MapsApiFailure
            }));
          });
      }, 350);
    };

    _onSelectLocation = location => {
      this._googleMapsApi.getPlaceDetails(location.placeId).then(
        place => {
          this.props.onSelectLocation(
            {
              ...location,
              ...place,
              phoneNumber: place.formatted_phone_number,
              formattedAddress: place.formatted_address
            },
            place
          );
        },
        () => {
          this.props.onSelectLocation(location, null);
        }
      );
    };

    _onEnterLocationManually = () => {
      this.props.onEnterLocationManually();
      this.setState(state => ({
        ...state,
        businessName: "",
        locations: null
      }));
    };

    _onCancelSelectLocation = () => {
      const { onCancelSelectLocation, onSelectLocation } = this.props;
      if (onCancelSelectLocation) {
        onCancelSelectLocation();
      } else {
        onSelectLocation(null);
      }
      this.setState({
        businessName: "",
        locations: null
      });
    };

    _onUseLocation = () => {
      this.props.onUseLocation();
    };
  };
}

function convertGmapsPlacesToLocations(googlePlaces = []) {
  return googlePlaces.map(place => ({
    name: place.structured_formatting.main_text,
    address: place.structured_formatting.secondary_text,
    placeId: place.place_id
  }));
}
